import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useMutation, useQuery, gql } from "@apollo/client";
import { format } from "date-fns";
import { ClipboardList } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

const ADD_COORDINATED_CARE_NOTE = gql`
  mutation AddCoordinatedCareNote($odrnumber: String!, $participant: String!, $note: AddCoordinatedCareNoteInput!) {
    addCoordinatedCareNote(odrnumber: $odrnumber, participant: $participant, note: $note) {
      message
    }
  }
`;

// Query to fetch Place of Service options from database
const GET_POS_OPTIONS = gql`
  query GetCalaimPosOptions {
    calaimPosOptions {
      pos_code
      pos_name
    }
  }
`;

// Hardcoded procedure types from billing_proceduretypes table
const PROCEDURE_TYPE_OPTIONS = [
  { value: "Initial Assessment-first 90 days", label: "Initial Assessment-first 90 days" },
  { value: "Care Plan-every quarter or more", label: "Care Plan-every quarter or more" },
  { value: "Client Contact", label: "Client Contact" },
  { value: "Client Outreach", label: "Client Outreach" },
  { value: "Care Coordination", label: "Care Coordination" },
];

const CARE_PHASE_OPTIONS = [
  { value: "enrolled", label: "Enrolled" },
  { value: "pre_enrollment_outreach", label: "Pre-Enrollment Outreach" },
];

const PROCEDURE_LENGTH_OPTIONS = [
  { value: "15", label: "15 mins" },
  { value: "30", label: "30 mins" },
  { value: "45", label: "45 mins" },
  { value: "60", label: "60 mins" },
  { value: "75", label: "1hour 15mins" },
  { value: "90", label: "1hour 30mins" },
  { value: "105", label: "1hour 45mins" },
  { value: "120", label: "2hours" },
  { value: "135", label: "2hours 15mins" },
  { value: "150", label: "2hours 30mins" },
  { value: "165", label: "2hours 45mins" },
  { value: "180", label: "3hours" },
  { value: "195", label: "3hours 15mins" },
  { value: "210", label: "3hours 30mins" },
  { value: "225", label: "3hours 45mins" },
  { value: "240", label: "4hours" }
];

const POS_NAME_TO_PLACE_OF_SERVICE: Record<string, string> = {
  "Home": "home",
  "Office": "office",
  "Telehealth in pt home": "telehealth_home",
  "Telehealth outside pt home": "telehealth_outside"
};

const resolvePlaceOfService = (
  posCode: string,
  posOptions?: { pos_code: string; pos_name: string }[]
) => {
  if (!posCode) {
    return "";
  }

  if (posCode === "0") {
    return "";
  }

  if (!posOptions) {
    return "";
  }

  const matchedPos = posOptions.find((option) => option.pos_code === posCode);
  if (!matchedPos) {
    return "";
  }

  return POS_NAME_TO_PLACE_OF_SERVICE[matchedPos.pos_name] || "";
};

interface CalAimEntryFormProps {
  odrNumber: string;
  participantId: string;
  user: any;
  showHeader?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const CalAimEntryForm: React.FC<CalAimEntryFormProps> = ({
  odrNumber,
  participantId,
  user,
  showHeader = true,
  onSuccess,
  onCancel
}) => {
  const { data: posData, loading: posLoading } = useQuery(GET_POS_OPTIONS);

  const [formData, setFormData] = useState({
    calaim_pos: "0",
    proceduremod1: "in_person", // Default based on PHP file: $proceduremod1_text = 'in_person';
    proceduremod2: "clinical",  // Default based on PHP file: $proceduremod2_text = 'clinical';
    care_phase: "enrolled",
    proceduretype: "none",
    procedurelength: "15",
    note_entry_date: format(new Date(), "yyyy-MM-dd"),
    procedurenote: "",
    procedureentery: user?.realname || user?.name || user?.username || "",
    fin: "", 
    isMdtConference: false
  });

  const [addCoordinatedCareNote, { loading }] = useMutation(ADD_COORDINATED_CARE_NOTE);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === "isMdtConference") {
      const isMdtConference = value === "true";
      setFormData((prev) => ({ 
        ...prev, 
        [name]: isMdtConference,
        // Note: PHP doesn't clear mod values but hides row if G9007 is checked.
        // We will keep values in state but handle visibility in render.
      }));
    } else if (name === "proceduremod1") {
       // Logic from PHP:
       // if ($proceduremod1_value == 'telehealth') { $proceduremod1 = 0; $proceduremod1_text = 'telehealth'; }
       // else { $proceduremod1_text = 'in_person'; }
       setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (name === "proceduremod2") {
       // Logic from PHP:
       // if ($proceduremod2_value == 'nonclinical') { $proceduremod2 = 0; $proceduremod2_text = 'nonclinical'; }
       // else { $proceduremod2_text = 'clinical'; }
       setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Logic from PHP:
    // if ($is_ecm_service && !$multidisciplinary_team_conf_checked) { ... }
    // Since this form IS the ECM form, we assume is_ecm_service is true.
    
    if (!formData.isMdtConference) {
      if (!formData.proceduremod1) {
        newErrors.proceduremod1 = "Please select In Person/Telehealth";
      }
      if (!formData.proceduremod2) {
        newErrors.proceduremod2 = "Please select Clinical/NonClinical";
      }
      
      // Validate Place of Service only if not in_person
      // if ($proceduremod1_value !== "in_person" && ($pos == 0 || $pos == null || empty($_POST['calaim_pos'])))
      if (formData.proceduremod1 !== "in_person" && (formData.calaim_pos === "0" || !formData.calaim_pos)) {
        newErrors.calaim_pos = "Please Select Place of Service";
      }
    }

    if (!formData.procedurelength) {
       newErrors.procedurelength = "Please Enter procedure length";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const posCode = formData.calaim_pos === "0" ? "" : formData.calaim_pos;
      const placeOfService = resolvePlaceOfService(posCode, posData?.calaimPosOptions);

      await addCoordinatedCareNote({
        variables: {
          odrnumber: odrNumber,
          participant: participantId,
          note: {
            service_type: "ECM",
            care_phase: formData.care_phase,
            place_of_service: placeOfService,
            pos_code: posCode,
            proceduremod1: formData.isMdtConference ? null : formData.proceduremod1,
            proceduremod2: formData.isMdtConference ? null : formData.proceduremod2,
            proceduretype: formData.proceduretype,
            procedurelength: formData.procedurelength,
            note_entry_date: formData.note_entry_date,
            procedurenote: formData.procedurenote,
            procedureentery: formData.procedureentery,
            isMdtConference: formData.isMdtConference
          },
        },
        refetchQueries: ["GetCoordinatedCareNotes", "GetSummaryData"],
        awaitRefetchQueries: true,
      });
      onSuccess();
    } catch (error) {
      console.error("Error adding ECM note:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showHeader && (
        <SectionHeader
          icon={ClipboardList}
          title="CalAIM Note"
          subtitle="Document ECM service activity"
        />
      )}
      
      {/* Top Row: Procedure Length (Left), Entered By (Right) */}
      <div className="grid grid-cols-2 gap-4">
         <div className="space-y-2">
          <Label htmlFor="procedurelength">Procedure Length</Label>
          <Select
            onValueChange={(value) => handleSelectChange("procedurelength", value)}
            value={formData.procedurelength}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select length" />
            </SelectTrigger>
            <SelectContent>
              {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.procedurelength && <p className="text-sm text-red-500">{errors.procedurelength}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="procedureentery">Entered By</Label>
          <Input
            id="procedureentery"
            name="procedureentery"
            value={formData.procedureentery}
            className="bg-gray-100"
            readOnly
            required
          />
        </div>
      </div>

      {/* MDT Conference Checkbox */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isMdtConference"
            checked={formData.isMdtConference}
            onCheckedChange={(checked) => handleSelectChange("isMdtConference", checked ? "true" : "false")}
          />
          <Label htmlFor="isMdtConference" className="font-medium">
            Multidisciplinary Team Conference (G9007)
          </Label>
        </div>
      </div>

      {/* Modifiers Row - Hidden if MDT Conference is checked */}
      {!formData.isMdtConference && (
        <div className="grid grid-cols-3 gap-4 border-t pt-4 mt-2">
          <div className="space-y-2">
            <Label>In Person/Telehealth</Label>
            <Select
                onValueChange={(value) => handleSelectChange("proceduremod1", value)}
                value={formData.proceduremod1}
            >
                <SelectTrigger className={errors.proceduremod1 ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="telehealth">Telehealth</SelectItem>
                </SelectContent>
            </Select>
            {errors.proceduremod1 && <p className="text-sm text-red-500">{errors.proceduremod1}</p>}
          </div>

          <div className="space-y-2">
            <Label>Clinical/NonClinical</Label>
             <Select
                onValueChange={(value) => handleSelectChange("proceduremod2", value)}
                value={formData.proceduremod2}
            >
                <SelectTrigger className={errors.proceduremod2 ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="clinical">Clinical</SelectItem>
                    <SelectItem value="nonclinical">NonClinical</SelectItem>
                </SelectContent>
            </Select>
            {errors.proceduremod2 && <p className="text-sm text-red-500">{errors.proceduremod2}</p>}
          </div>

          {/* Place of Service - Only visible/required if NOT "in_person" */}
          {formData.proceduremod1 !== 'in_person' && (
             <div className="space-y-2">
              <Label htmlFor="calaim_pos">Place of Service</Label>
              <Select 
                onValueChange={(value) => handleSelectChange("calaim_pos", value)}
                value={formData.calaim_pos}
              >
                <SelectTrigger className={errors.calaim_pos ? "border-red-500" : ""}>
                  <SelectValue placeholder="Select place of service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">-- Select an option --</SelectItem>
                  {posLoading ? (
                    <SelectItem value="loading">Loading...</SelectItem>
                  ) : (
                    posData?.calaimPosOptions
                      ?.filter((option: { pos_code: string; pos_name: string }) => 
                        option.pos_name === "Home" || 
                        option.pos_name === "Office" ||
                        option.pos_name === "Telehealth in pt home" ||
                        option.pos_name === "Telehealth outside pt home"
                      )
                      ?.map((option: { pos_code: string; pos_name: string }) => (
                        <SelectItem key={option.pos_code} value={option.pos_code}>
                          {option.pos_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {errors.calaim_pos && <p className="text-sm text-red-500">{errors.calaim_pos}</p>}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 border-t pt-4 mt-2">
        <div className="space-y-2">
          <Label>Member Status</Label>
          <Select
            onValueChange={(value) => handleSelectChange("care_phase", value)}
            value={formData.care_phase}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {CARE_PHASE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Procedure Note */}
      <div className="space-y-2">
        <Label htmlFor="procedurenote">Procedure Note</Label>
        <Textarea
          id="procedurenote"
          name="procedurenote"
          value={formData.procedurenote}
          onChange={handleInputChange}
          placeholder="Enter procedure note..."
          className="min-h-[150px]"
          required
        />
      </div>

      <div className="flex items-end justify-between space-x-2 mt-6 pt-4 border-t">
        {/* Entry Date moved to footer */}
        <div className="space-y-2 w-1/3">
            <Label htmlFor="note_entry_date">Note Date</Label>
            <Input
            type="date"
            id="note_entry_date"
            name="note_entry_date"
            value={formData.note_entry_date}
            onChange={handleInputChange}
            required
            />
        </div>

        <div className="flex space-x-2">
            <Button size="sm" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Note"}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} type="button">
            Cancel
            </Button>
        </div>
      </div>
    </form>
  );
};
